import { useEffect } from "react";

import { isListeningTo, stopListening } from "./listening";
import type { Room } from "./api";

/**
 * The stream itself, when the host is simulcasting to Twitch.
 *
 * ## Why embed rather than link out
 *
 * `WatchElsewhere` sends people away, which is the right answer for a platform
 * that cannot be embedded and the wrong one for Twitch, which can. Sending an
 * audience to another tab costs the room its queue: they cannot submit a track
 * or boost one from over there, and a listener who leaves usually does not come
 * back mid-set. Embedded, the show is on Twitch and the queue is still here.
 *
 * ## `parent` is not optional and cannot be guessed
 *
 * Twitch refuses to frame either embed unless `parent` names the exact hostname
 * of the page doing the framing - not the origin, not a path, the hostname. So
 * it is read from `window.location` rather than from `brand.domain`: those
 * agree in production and disagree on every developer's machine, in preview
 * builds, and behind the desktop shell.
 *
 * ## Two embeds, one of them the whole point
 *
 * The chat embed is the reason this exists. A host on Twitch is reading that
 * chat, and until now the room answered by pointing at the door. Verified
 * against a real channel: the chat frame loads, authenticates the viewer's own
 * Twitch session if they have one, and takes messages.
 */

/**
 * The channel name out of a profile link, or null.
 *
 * The URL was pinned to `twitch.tv` by the server when the link was saved - see
 * `ProfileLinks` - so this only has to find the handle, not decide whether to
 * trust the host. Null for anything shaped unexpectedly, which falls back to
 * the link-out rather than framing a guess.
 */
export function twitchChannel(room: Room): string | null {
  if (room.simulcast?.platform !== "twitch") return null;

  try {
    const path = new URL(room.simulcast.url).pathname.split("/").filter(Boolean);
    const channel = path[0];

    return channel && /^[A-Za-z0-9_]{3,25}$/.test(channel) ? channel : null;
  } catch {
    return null;
  }
}

/** The hostname Twitch has to be told about. See the note above. */
function parent(): string {
  return typeof window === "undefined" ? "localhost" : window.location.hostname;
}

export function TwitchPlayer({ channel, room }: { channel: string; room: Room }) {
  /*
   * One source of sound.
   *
   * The set is going out twice - through our player from the catalogue, and
   * through Twitch a second or two behind it - and hearing both at once is
   * genuinely unpleasant. `WatchElsewhere` already stops the audio when
   * somebody clicks through; the equivalent moment here is the stream
   * appearing, because that is when they have arrived.
   */
  useEffect(() => {
    if (isListeningTo(room.username)) stopListening();
  }, [room.username]);

  return (
    <div
      data-twitch-player={channel}
      className="relative w-full overflow-hidden rounded-[var(--radius-panel)] border border-line bg-black"
      style={{ aspectRatio: "16 / 9" }}
    >
      <iframe
        title={`${room.host.display_name} on Twitch`}
        src={`https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${parent()}&autoplay=true`}
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
}

export function TwitchChat({ channel }: { channel: string }) {
  return (
    <iframe
      title="Twitch chat"
      data-twitch-chat={channel}
      /*
       * `darkpopout` regardless of our theme.
       *
       * The live room is near-black in both themes - the veils over the host's
       * banner are `theme:keep` - so a light chat column beside it would be the
       * one bright rectangle on a dark stage. See the stage palette in
       * `index.css`.
       */
      src={`https://www.twitch.tv/embed/${encodeURIComponent(channel)}/chat?parent=${parent()}&darkpopout`}
      className="h-full w-full flex-1 border-0"
    />
  );
}
